if (!customElements.get('lagoon-product')) {
  customElements.define('lagoon-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-product-json]').textContent);
      this.product = this.config.product;
      this.strings = this.config.strings;
      this.benefit = 'discount';
      this.benefitError = false;
      this.cartRoot = window.Shopify?.routes?.root || '/';
      this.form = this.querySelector('.lagoon-product-form');
      this.quantity = this.form.querySelector('[name="quantity"]');
      this.variantInput = this.form.querySelector('[data-variant-id]');
      this.variantInput.disabled = false;
      this.variant = this.product.variants.find(v => String(v.id) === String(this.config.selectedId));
      this.hero = this.querySelector('.hero');
      this.photo = this.hero.querySelector('img');
      this.media = this.config.media;
      this.photoMetadata = {};
      this.busy = false;
      this.uncertain = false;
      this.currentMedia = null;
      this.formatMoney = cents => new Intl.NumberFormat(this.config.locale + '-MX', { style: 'currency', currency: this.config.currency }).format(cents / 100);
      const opts = { signal: this.abort.signal };
      const drawer = document.querySelector('cart-drawer');
      drawer?.addEventListener('click', event => {
        if (!event.target.closest('.wt-cart__drawer__close')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (drawer.isOpen) drawer.toggleDrawerClasses();
      }, { ...opts, capture: true });
      this.addEventListener('click', event => this.onClick(event), opts);
      this.quantity.addEventListener('input', () => this.updateQuantity(), opts);
      this.addEventListener('change', event => {
        if (event.target.matches('input[type="radio"]')) this.chooseVariant();
        if (event.target === this.quantity) this.updateQuantity();
      }, opts);
      this.form.addEventListener('submit', event => this.submit(event), opts);
      this.querySelector('.gallery').addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.movePhoto(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, opts);
      this.hero.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' && !event.target.closest('button')) this.touchStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      }, opts);
      this.hero.addEventListener('pointerup', event => {
        if (!this.touchStart || event.pointerId !== this.touchStart.id) return;
        const dx = event.clientX - this.touchStart.x, dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.movePhoto(dx < 0 ? 1 : -1);
      }, opts);
      this.hero.addEventListener('pointercancel', () => { this.touchStart = null; }, opts);
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => {
          if (!this.querySelector('dialog[open]')) document.body.style.overflow = this.previousOverflow || '';
          this.dialogOpener?.focus();
        }, opts);
        dialog.addEventListener('click', event => { if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); } }, opts);
      });
      window.addEventListener('popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        const variant = this.product.variants.find(v => String(v.id) === id);
        if (variant) { this.variant = variant; this.syncOptionInputs(); this.renderVariant(false); }
      }, opts);
      this.resizeObserver = new ResizeObserver(() => this.fitPhoto());
      this.resizeObserver.observe(this.hero);
      this.stickyObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        this.querySelector('[data-sticky]').classList.toggle('visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0);
      });
      this.stickyObserver.observe(this.form.querySelector('.actions'));
      this.syncOptionInputs();
      this.renderVariant(false);
      if (this.config.campaign?.visible) this.restoreBenefit();
      fetch(this.dataset.photoMeta, { signal: this.abort.signal })
        .then(response => { if (!response.ok) throw new Error('Photo metadata unavailable'); return response.json(); })
        .then(data => {
          this.photoMetadata = data;
          if (this.currentMedia) this.photo.alt = data[this.filename(this.currentMedia.src)]?.alt || this.currentMedia.alt || this.product.title;
          this.fitPhoto();
        })
        .catch(error => { if (error.name !== 'AbortError') console.warn('Lagoon gallery uses contain fallback:', error.message); });
    }

    disconnectedCallback() {
      this.abort?.abort(); this.resizeObserver?.disconnect(); this.stickyObserver?.disconnect();
      if (this.querySelector('dialog[open]')) document.body.style.overflow = this.previousOverflow || '';
    }

    syncOptionInputs() {
      this.querySelectorAll('[data-option-index]').forEach(fieldset => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input').forEach(input => { input.checked = input.value === this.variant?.options[index]; });
      });
    }

    chooseVariant() {
      if (this.busy) return;
      this.dispatchEvent(new CustomEvent('variantChangeStart', { bubbles: true, composed: true }));
      const options = [...this.querySelectorAll('[data-option-index]')].map(fieldset => fieldset.querySelector('input:checked')?.value);
      this.variant = this.product.variants.find(variant => variant.options.every((value, index) => value === options[index]));
      this.uncertain = false;
      this.setStatus('');
      this.renderVariant(true);
      this.dispatchEvent(new CustomEvent('variantChangeEnd', { bubbles: true, composed: true }));
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.section, html: document, variant: this.variant } });
      }
    }

    renderVariant(updateURL) {
      this.variantInput.value = this.variant?.id || '';
      this.variantInput.disabled = !this.variant;
      this.querySelectorAll('[data-option-index]').forEach(fieldset => {
        const selected = fieldset.querySelector('input:checked');
        fieldset.querySelector('[data-option-label]').textContent = selected?.value || '';
        const optionIndex = Number(fieldset.dataset.optionIndex);
        fieldset.querySelectorAll('input').forEach(input => {
          const matching = this.product.variants.filter(variant => variant.options[optionIndex] === input.value);
          input.closest('.swatch').classList.toggle('is-unavailable', !matching.some(variant => variant.available));
        });
      });
      const available = Boolean(this.variant?.available);
      const availability = this.querySelector('[data-availability]');
      availability.hidden = available;
      availability.textContent = this.variant ? this.strings.soldOut : this.strings.unavailable;
      const campaignSale = this.discountSelected();
      const unitPrice = this.unitPrice();
      this.querySelector('[data-price]').textContent = this.variant ? this.formatMoney(unitPrice) : '—';
      this.querySelector('[data-sticky-price]').textContent = this.variant ? this.formatMoney(unitPrice) : '—';
      const compare = this.querySelector('[data-compare-price]');
      const onSale = campaignSale || this.variant?.compare_at_price > this.variant?.price;
      compare.hidden = !onSale;
      compare.textContent = onSale ? this.formatMoney(campaignSale ? this.variant.price : this.variant.compare_at_price) : '';
      this.querySelector('[data-price-label]').textContent = campaignSale ? this.config.campaign.priceLabel : onSale ? this.strings.salePrice : this.strings.regularPrice;
      const rules = this.variant?.quantity_rule || { min: 1, increment: 1 };
      this.quantity.min = rules.min || 1; this.quantity.step = rules.increment || 1;
      if (rules.max) this.quantity.max = rules.max; else this.quantity.removeAttribute('max');
      if (Number(this.quantity.value) < Number(this.quantity.min)) this.quantity.value = this.quantity.min;
      this.updateQuantity();
      const mediaId = this.variant?.featured_media?.id || this.variant?.featured_image?.id;
      const match = this.media.find(media => String(media.id) === String(mediaId)) || this.media.find(media => this.sameFile(media.src, this.variant?.featured_image?.src)) || this.media[0];
      if (match) this.showPhoto(match.id, false);
      if (updateURL && this.variant) {
        const url = new URL(location.href); url.searchParams.set('variant', this.variant.id); history.replaceState({}, '', url);
      }
    }

    updateQuantity() {
      const value = Number(this.quantity.value), valid = this.quantity.checkValidity() && Number.isInteger(value) && value > 0;
      this.querySelector('[data-action="minus"]').disabled = this.busy || value <= Number(this.quantity.min);
      this.querySelector('[data-action="plus"]').disabled = this.busy || Boolean(this.quantity.max && value >= Number(this.quantity.max));
      this.querySelector('[data-selection]').textContent = this.variant && valid ? this.strings.selection.replace('[quantity]', value).replace('[total]', this.formatMoney(this.unitPrice() * value)) : this.strings.invalidQuantity;
      this.querySelectorAll('[data-benefit]').forEach(button => { button.disabled = this.busy; button.setAttribute('aria-pressed', String(button.dataset.benefit === this.benefit)); });
      this.querySelectorAll('[data-add]').forEach(button => {
        button.disabled = this.busy || this.uncertain || this.benefitError || !valid || !this.variant?.available;
        button.setAttribute('aria-busy', String(this.busy));
        button.textContent = this.busy ? this.strings.loading : !this.variant ? this.strings.unavailable : this.variant.available ? this.strings.add : this.strings.soldOut;
      });
    }

    onClick(event) {
      const closer = event.target.closest('[data-close]');
      if (closer) { closer.closest('dialog').close(); return; }
      const button = event.target.closest('[data-action]');
      if (!button) return;
      const action = button.dataset.action;
      if (action === 'benefit') { this.chooseBenefit(button.dataset.benefit); return; }
      if (action === 'retry-benefit') { this.chooseBenefit(this.benefit); return; }
      if (action === 'photo') this.showPhoto(button.dataset.mediaId);
      if (action === 'minus' || action === 'plus') {
        if (this.busy) return;
        if (action === 'minus') this.quantity.stepDown(); else this.quantity.stepUp();
        this.updateQuantity();
      }
      if (action === 'zoom' || action === 'guide') {
        const dialog = this.querySelector('[data-dialog="' + action + '"]');
        if (action === 'zoom' && this.currentMedia) {
          dialog.querySelector('[data-zoom-image]').src = this.currentMedia.original;
          dialog.querySelector('[data-zoom-image]').alt = this.photo.alt;
          dialog.querySelector('[data-zoom-caption]').textContent = this.photo.alt;
        }
        this.dialogOpener = button; this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden'; dialog.showModal();
      }
    }

    discountSelected() {
      const campaign = this.config.campaign;
      const now = Date.now();
      return Boolean(this.variant && campaign?.active && this.benefit === 'discount' && now >= Date.parse(campaign.startsAt) && now < Date.parse(campaign.endsAt));
    }

    unitPrice() {
      return this.variant ? this.discountSelected() ? Math.round(this.variant.price * (1 - this.config.campaign.rate)) : this.variant.price : 0;
    }

    async restoreBenefit() {
      try {
        const response = await fetch(this.cartRoot + 'cart.js', { signal: this.abort.signal });
        if (!response.ok) return;
        const cart = await response.json();
        if (this.busy || this.benefitTouched) return;
        if (cart.attributes?.lagoon_buen_fin === 'msi') this.benefit = 'msi';
        this.renderVariant(false);
      } catch (error) { if (error.name !== 'AbortError') console.warn('Lagoon benefit selection:', error.message); }
    }

    async applyBenefit(cartDrawer, addedId) {
      const campaign = this.config.campaign;
      if (!campaign?.visible) return null;
      const sections = cartDrawer?.getSectionsToRender?.() || [];
      const requestedCode = this.discountSelected() ? campaign.code : '';
      const response = await fetch(this.cartRoot + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: requestedCode, attributes: { lagoon_buen_fin: this.benefit }, sections: sections.map(section => section.id), sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(campaign.error);
      const applied = data.discount_codes?.some(discount => discount.code?.toUpperCase() === campaign.code.toUpperCase() && discount.applicable);
      const hasCampaign = data.cart_level_discount_applications?.some(discount => discount.title?.toUpperCase() === campaign.code.toUpperCase());
      if (requestedCode && data.item_count > 0 && !(applied || hasCampaign)) throw new Error(campaign.error);
      if (!requestedCode && (applied || hasCampaign)) throw new Error(campaign.error);
      this.benefitError = false;
      this.querySelector('[data-action="retry-benefit"]').hidden = true;
      if (addedId) data.id = addedId;
      if (cartDrawer && sections.every(section => data.sections?.[section.id])) cartDrawer.renderContents(data, Boolean(addedId) && !cartDrawer.isOpen);
      if (!addedId && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: data });
      return data;
    }

    async chooseBenefit(benefit) {
      if (this.busy || !['discount', 'msi'].includes(benefit)) return;
      this.benefitTouched = true;
      this.benefit = benefit;
      this.busy = true;
      this.renderVariant(false);
      this.setStatus('');
      try {
        await this.applyBenefit(document.querySelector('cart-drawer'));
        this.setStatus(benefit === 'discount' ? this.config.campaign.discountLabel : this.config.campaign.msiLabel);
      } catch (error) {
        this.benefitError = true;
        this.setStatus(this.config.campaign.error, true);
        this.querySelector('[data-action="retry-benefit"]').hidden = false;
      } finally { this.busy = false; this.updateQuantity(); }
    }

    sameFile(a, b) { return a && b && this.filename(a) === this.filename(b); }
    filename(url) { return new URL(url, location.href).pathname.split('/').pop(); }

    showPhoto(id, scroll = true) {
      const media = this.media.find(item => String(item.id) === String(id));
      if (!media || !this.photo) return;
      this.currentMedia = media;
      this.fitPhoto();
      if (media.srcset) this.photo.setAttribute('srcset', media.srcset);
      else this.photo.removeAttribute('srcset');
      if (this.photo.src !== media.src) this.photo.src = media.src;
      this.photo.alt = this.photoMetadata[this.filename(media.src)]?.alt || media.alt || this.product.title;
      this.querySelectorAll('.extra-media').forEach(element => { element.hidden = element.dataset.mediaId !== String(id); if (element.hidden) element.querySelectorAll('video').forEach(video => video.pause()); });
      this.hero.hidden = media.type !== 'image';
      this.querySelectorAll('.thumbs button').forEach(button => {
        const selected = button.dataset.mediaId === String(id);
        button.classList.toggle('active', selected); button.setAttribute('aria-current', String(selected));
        if (selected && scroll) {
          const strip = button.parentElement;
          const rect = button.getBoundingClientRect(), container = strip.getBoundingClientRect();
          if (rect.left < container.left || rect.right > container.right) strip.scrollLeft += rect.left - container.left - (strip.clientWidth - button.clientWidth) / 2;
        }
      });
      this.fitPhoto();
    }

    movePhoto(delta) {
      const index = this.media.indexOf(this.currentMedia);
      const next = this.media[(index + delta + this.media.length) % this.media.length];
      if (next) this.showPhoto(next.id);
    }

    fitPhoto() {
      if (!this.currentMedia || !this.photo) return;
      const metadata = this.photoMetadata[this.filename(this.currentMedia.src)];
      if (!metadata || metadata.ambient) {
        this.photo.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;max-width:none;object-fit:contain';
        this.photo.sizes = Math.max(1, Math.ceil(this.hero.clientWidth)) + 'px';
        return;
      }
      const [x1,y1,x2,y2] = metadata.bbox, bw = x2-x1, bh = y2-y1;
      const width = this.hero.clientWidth, height = this.hero.clientHeight;
      const ratio = Math.min(width * .8 / bw, height * .88 / bh);
      this.photo.sizes = Math.max(1, Math.ceil(metadata.w * ratio)) + 'px';
      this.photo.style.cssText = `position:absolute;left:${(width-bw*ratio)/2-x1*ratio}px;top:${(height-bh*ratio)/2-y1*ratio}px;width:${metadata.w*ratio}px;height:${metadata.h*ratio}px;max-width:none;object-fit:contain`;
    }

    setStatus(text, error = false) {
      const status = this.querySelector('[data-status]');
      status.textContent = text; status.hidden = !text; status.classList.toggle('error', error);
      status.setAttribute('role', error ? 'alert' : 'status');
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.uncertain || this.benefitError || !this.variant?.available || !this.form.reportValidity()) return;
      this.busy = true; this.updateQuantity(); this.setStatus('');
      this.quantity.disabled = true; this.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = true; });
      const variantId = this.variant.id, quantity = Number(this.quantity.value);
      const cart = document.querySelector('cart-drawer');
      cart?.setActiveElement?.(document.activeElement);
      const body = new FormData(this.form); body.set('id', variantId); body.set('quantity', quantity);
      const sections = cart?.getSectionsToRender?.() || [];
      if (sections.length) body.set('sections', sections.map(section => section.id).join(','));
      body.set('sections_url', window.location.pathname);
      let confirmed = false;
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        let data = await response.json();
        if (!response.ok || data.status) {
          const message = data.description || data.message || this.strings.error;
          this.setStatus(message, true);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: variantId, errors: message, message });
          return;
        }
        confirmed = true;
        if (this.config.campaign?.visible) {
          const updated = await this.applyBenefit(cart, variantId);
          if (updated) data = updated;
        }
        this.setStatus(this.strings.added); this.querySelector('[data-cart-link]').hidden = false;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: data });
        if (!this.config.campaign?.visible && cart?.dataset.cartType === 'drawer' && sections.every(section => data.sections?.[section.id])) {
          try { cart.renderContents(data, !cart.isOpen); }
          catch (error) { console.warn('Lagoon cart confirmation is available through the cart link:', error.message); }
        } else if (cart?.dataset.cartType === 'page') {
          window.location.assign(window.routes.cart_url);
        }
      } catch (error) {
        if (confirmed) {
          this.benefitError = true;
          this.setStatus(this.config.campaign.addedError, true);
          this.querySelector('[data-action="retry-benefit"]').hidden = false;
          this.querySelector('[data-cart-link]').hidden = false;
        } else {
          this.uncertain = true; this.setStatus(this.strings.error, true);
          this.querySelector('[data-cart-link]').hidden = false;
        }
        console.warn('Lagoon cart request:', error.message);
      } finally {
        this.busy = false; this.quantity.disabled = false;
        this.querySelectorAll('input[type="radio"]').forEach(input => { input.disabled = false; });
        this.updateQuantity();
      }
    }
  });
}

